<template>
  <mdb-container class="mt-2">

    <!--    Page main section-->

    <jumbotron :subtitle="$t('scheduleDuration.subtitle')" :title="$t('scheduleDuration.title')"></jumbotron>

    <!--    Buttons-->
    <mdb-row class="justify-content-center mt-3">
      <mdb-col col="sm-6">

        <div class="input-border">

          <div>{{ $tc('general.date.from') }}</div>
          <!-- https://michael-loy.atlassian.net/browse/LOC-5324 -->
          <VueDatePicker v-model="date.from"
                         :format="date.format"
                         :locale="locale"
                         :minDate="minDate" 
                         clearable
                         format-header="dddd, DD.MMMM"
                         placeholder=" "
                         @onChange="updateStoreDateFromDuration"
          />
        </div>
      </mdb-col>

      <mdb-col col="sm-6">

        <div class="input-border mt-4 mt-sm-0" :class="{ 'border-danger' : bookingBlocked }">

          <div>{{ $tc('general.date.to') }}</div>
          <VueDatePicker v-model="date.to"
                         :format="date.format"
                         :locale="locale"
                         :minDate="date.from ? date.from : null"
                         clearable
                         format-header="dddd, DD.MMMM"
                         placeholder=" "
                         @onChange="updateStoreDateToDuration"
          />
        </div>
      </mdb-col>
      
      <mdb-col col="sm-12 mt-0 mt-sm-3" v-if="bookingBlocked">
        <div class="border border-danger">
          {{ $tc('scheduleDuration.bookingBlocked') }}
        </div>
      </mdb-col>

      <mdb-col col="sm-6 mt-0 mt-sm-3">

        <div class="input-border mt-4 mt-sm-0">

          <div>{{ $tc('general.time.from') }}</div>
          <VueTimepicker v-model="time.from"
                         :format="$i18n.locale === 'de' ? 'HH:mm' : 'hh:mm A'"
                         :minute-interval="30"
                         auto-scroll
                         close-on-complete
                         lazy
                         placeholder=" "
                         @change="updateStoreTimeFromDuration"/>
        </div>
      </mdb-col>
      <mdb-col col="sm-6 mt-0 mt-sm-3">

        <div class="input-border mt-4 mt-sm-0">

          <div>{{ $tc('general.time.to') }}</div>
          <VueTimepicker v-model="time.to"
                         :format="$i18n.locale === 'de' ? 'HH:mm' : 'hh:mm A'"
                         :minute-interval="30"
                         auto-scroll
                         close-on-complete
                         lazy
                         placeholder=" "
                         @change="updateStoreTimeToDuration"/>
        </div>
      </mdb-col>


    </mdb-row>

    <!--    Next button-->
    <mdb-row v-if="canProceed" class="justify-content-center mt-3">
      <mdb-col>
        <mdb-btn block class="large-button btn-primary" @click="next" :disabled="bookingBlocked">
          {{ $t('general.next') }}
        </mdb-btn>
      </mdb-col>
    </mdb-row>

  </mdb-container>
</template>

<script>

import {mapActions, mapGetters} from "vuex";

export default {
  name: "ScheduleDuration",

  metaInfo() {
    return {
      title: this.$t('scheduleDuration.title')
    }
  },

  data: () => {
    return {
      locale: {
        lang: 'de'
      },
      date: {
        from: null,
        to: null,
        format: 'DD.MM.YYYY'
      },
      time: {
        from: null,
        to: null,
        format: 'HH:mm'
      },
      recurringBlockAccountIds: [ 9398 ]
    }
  },

  mounted() {

    this.init();

  },
  computed: {
    ...mapGetters({duration: 'getDuration', accountId: 'getAccountId'}),

    moment() {
      return moment;
    },

    minDate() {
      return moment().toISOString()
    },

    canProceed() {
      return this.duration.date_from && this.duration.date_to && this.duration.time_from && this.duration.time_to
    },
    
    bookingBlocked(){
      
      if (this.accountId && this.recurringBlockAccountIds.includes(this.accountId)){
       // find date difference in days with moment without time
        let diff = moment(moment(this.date.to, 'YYYY-MM-DD')).diff(moment(moment(this.date.from, 'YYYY-MM-DD')), 'days');
        return diff > 0;
      }
      return false;
    }

  },

  methods: {

    ...mapActions(["updateDuration"]),

    init() {

      this.locale.lang = this.$i18n.locale;

      /**
       * https://michael-loy.atlassian.net/browse/LOC-4034
       * Change time format from 12hr AM/PM to 24hr
       */
      if (this.locale.lang === 'de' && false) {
        this.time.format = 'hh:mm A';
      }
      
      /** https://michael-loy.atlassian.net/browse/LOC-5329 */
      if (this.accountId === 9398){
          this.time.from = '08:00';
          this.updateStoreTimeFromDuration({ data: { HH : '08' , mm: '00' } })
          this.time.to = '17:00'
          this.updateStoreTimeToDuration({ data: { HH : '17' , mm: '00' } })
      }

      this.date.from = this.duration.date_from;
      this.date.to = this.duration.date_to;
      if (this.duration.time_from) {
        // convert time_from to time.format
        this.time.from = moment(this.duration.time_from, 'HH:mm').format(this.time.format);
      }
      if (this.duration.time_to) {
        // convert time_to to time.format
        this.time.to = moment(this.duration.time_to, 'HH:mm').format(this.time.format);
      }

      if (!this.date.from) {
        this.date.from = moment().add(1, 'days').toISOString(); // https://michael-loy.atlassian.net/browse/LOC-5327
      }
      if (!this.date.to) {
        // set to date after 1 week from now
        // this.date.to = moment(this.date.from).add(7, 'days').toISOString();
        /**
         * https://michael-loy.atlassian.net/browse/LOC-4778
         * https://michael-loy.atlassian.net/browse/LOC-5327
         * @type {string}
         */
        this.date.to = moment().add(1, 'days').toISOString();
      }

      this.duration.date_from = this.date.from;
      this.duration.date_to = this.date.to;

      this.updateDuration(this.duration);

    },

    updateStoreDateFromDuration() {
      this.duration.date_from = this.date.from;
      // check if the from date is after to date
      if (moment(this.date.to).isBefore(this.date.from)) {
        /**
         * https://michael-loy.atlassian.net/browse/LOC-5328
         * https://michael-loy.atlassian.net/browse/LOC-5323
         * @type {string}
         */
        this.date.to = moment(this.date.from).add(0, 'days').toISOString();
        this.updateStoreDateToDuration();
      }
    },
    updateStoreDateToDuration() {
      this.duration.date_to = this.date.to;
    },

    updateStoreTimeFromDuration(time) {
      let time_from = `${time.data.HH}:${time.data.mm}`;
      if (!(/[a-zA-Z]/.test(time_from))) { // accept only "00:00" format
        this.duration.time_from = time_from;
        this.updateDuration(this.duration);
      }
    },
    updateStoreTimeToDuration(time) {
      let time_to = `${time.data.HH}:${time.data.mm}`;
      if (!(/[a-zA-Z]/.test(time_to))) { // accept only "00:00" format
        this.duration.time_to = time_to;
        this.updateDuration(this.duration);
      }
    },

    next() {
      this.$router.push({name: 'SlotAvailability'})
    }

  }
}
</script>

<style lang="scss">
@import "_index.scss";
</style>



// WEBPACK FOOTER //
// src/components/pages/schedule-duration/index.vue